import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { apiGet, apiPatch, apiPost, TENANT_TOKEN_KEY } from '../../api/client';
import { resolveWidgetMediaUrl } from '../../lib/resolveWidgetMediaUrl';
import { showGrowl } from '../../ui/growlState';
import { agentSourceLabel, channelLabel, formatAgentModel } from './conversationChannelMeta';
import { ConversationChannelIcon } from './ConversationChannelIcon';
import { useTenantPermissions } from '../../auth/TenantPermissionsContext';
import { useAgentSelector } from '../../auth/AgentSelectorContext';
import { Button, Input, Modal } from '../../design-system';
import { tokens } from '../../design-system/tokens';
import { DemoImageAlbum } from '../../demo/DemoImageAlbum';
import { formatMessage } from '../../demo/formatMessage';
import { InlineAudioPlayer } from './InlineAudioPlayer';
import { LeadQualityEditor } from './LeadQualityEditor';
import {
  groupConsoleImageMessages,
  imageAttachmentsOf,
} from './groupConsoleImageMessages';

type Msg = {
  id: string;
  sender: string;
  content: string;
  /** URLs firmadas para adjuntos (imagen / video / audio) desde la API. */
  attachments?: Array<{ kind: string; mimeType: string; caption?: string; url?: string; ref?: string; durationSeconds?: number }>;
  /** true: respuesta por similitud con la guía (sin LLM); en consola se muestra con prefijo * */
  semanticShortcut?: boolean;
  timestamp: string;
  agentSource: string | null;
  agentModel: string | null;
  /** Modelo intentado cuando hubo reserva (p. ej. Claude). */
  agentModelRequested?: string | null;
  usedModelFallback?: boolean;
  /** Correcciones que el verificador hizo al texto del modelo (seña, horarios). */
  ruleCorrections?: Array<{ ruleTitle: string | null; summary: string; before: string; after: string }> | null;
  /** Resultado de la auditoría asíncrona de reglas blandas. */
  ruleAudit?: { violations: Array<{ ruleId: string; title: string | null; reason: string }> } | null;
  isWhisper?: boolean;
  /** good | bad — valoración del operador en consola. */
  operatorRating?: string | null;
  operatorFeedbackDesired?: string | null;
  operatorFeedbackComment?: string | null;
  /** Texto crudo devuelto por Whisper (preservado aun si content se modifica). */
  audioTranscription?: string | null;
  /** Estado de transcripción de audio: 'success' | 'failed' | 'duration_exceeded' | 'download_failed' | 'disabled' */
  transcriptionStatus?: string | null;
};

type ContactBlacklistScope = 'thread' | 'integration' | 'channel';

type Conv = {
  id: string;
  controlMode: string;
  tags: string[];
  channel: string;
  channelIntegrationId?: string | null;
  externalThreadId?: string | null;
  visitorName: string | null;
  /** ISO: última actualización del hilo (sinc. con API). */
  updatedAt: string;
  /** Visitante escribiendo (widget, demo, Meta, testchat simulado, etc.). */
  visitorTyping?: boolean;
  messages: Msg[];
  virtualAgent: { id: string; name: string } | null;
  responseGuideTargetAgent?: { id: string; name: string } | null;
  workflow: { id: string; name: string; isActive: boolean } | null;
  /** Reserva con comprobante de seña pendiente de confirmación humana (calendario). */
  calendarDepositReview?: {
    id: string;
    virtualAgentId: string;
    status: string;
    depositReceiptUrl: string | null;
    customerName: string | null;
    resource: { name: string } | null;
  } | null;
  leadQuality?: string | null;
  leadReason?: string | null;
  leadFitDetails?: string[];
  leadNote?: string | null;
  leadRatedSource?: string | null;
  leadMode?: 'ads' | 'internal' | null;
  leadStates?: Array<{ id: string; label: string; color?: string }> | null;
  hasLeadQualificationPlugin?: boolean;
};

function learningTargetAgent(conv: Conv): { id: string; name: string } | null {
  return conv.responseGuideTargetAgent ?? conv.virtualAgent;
}

/** Fecha/hora a mostrar arriba a la derecha: último mensaje o, si no hay, `updatedAt`. */
type OutletCtx = { refreshList: () => void };

function lastVisitorTextBefore(messages: Msg[], endIndex: number): string {
  for (let i = endIndex - 1; i >= 0; i--) {
    const x = messages[i]!;
    if (x.isWhisper) continue;
    if (x.sender === 'visitor') return x.content;
  }
  return '';
}

/** Día local YYYY-MM-DD para agrupar. */
function localDayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isLocalToday(iso: string, now: Date): boolean {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

/** Título de separador: Hoy, Ayer, o fecha larga. */
function daySeparatorLabel(iso: string, now: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startThat = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startToday.getTime() - startThat.getTime()) / 86400000);
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' });
  }
  return d.toLocaleDateString('es', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Pie de burbuja: solo hora si es hoy; si no, fecha breve + hora. */
function messageDateTimeLine(iso: string, now: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
  if (isLocalToday(iso, now)) return time;
  const shortDate = d.toLocaleDateString('es', {
    day: 'numeric',
    month: 'short',
    year: d.getFullYear() !== now.getFullYear() ? '2-digit' : undefined,
  });
  return `${shortDate} · ${time}`;
}

const daySepWrap: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  margin: '4px 0 14px',
  width: '100%',
  flexShrink: 0,
};

const daySepLine: CSSProperties = {
  flex: 1,
  height: 1,
  background: 'linear-gradient(90deg, transparent, #e2e8f0, transparent)',
};

const daySepPill: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#64748b',
  padding: '4px 12px',
  borderRadius: 999,
  background: '#f1f5f9',
  border: '1px solid #e2e8f0',
  whiteSpace: 'nowrap' as const,
};

const messageTimeTop: CSSProperties = {
  fontSize: 10,
  opacity: 0.82,
  fontWeight: 500,
  letterSpacing: 0.02,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
};

async function filesToDemoAttachmentPayload(
  files: File[]
): Promise<Array<{ mimeType: string; base64: string; fileName: string }>> {
  return Promise.all(
    files.map(
      (file) =>
        new Promise<{ mimeType: string; base64: string; fileName: string }>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => {
            const s = String(r.result);
            const m = /^data:([^;]+);base64,(.+)$/.exec(s);
            if (!m) reject(new Error('No se pudo leer el archivo'));
            else resolve({ mimeType: m[1], base64: m[2], fileName: file.name });
          };
          r.onerror = () => reject(r.error ?? new Error('No se pudo leer el archivo'));
          r.readAsDataURL(file);
        })
    )
  );
}

export function ConversationChatPane() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const mensajeId = searchParams.get('mensaje');
  const { refreshList } = useOutletContext<OutletCtx>();
  const token = localStorage.getItem(TENANT_TOKEN_KEY)!;
  const [conv, setConv] = useState<Conv | null>(null);
  const [err, setErr] = useState('');
  const [visitorMsg, setVisitorMsg] = useState('');
  const [visitorFiles, setVisitorFiles] = useState<File[]>([]);
  const [agentMsg, setAgentMsg] = useState('');
  const [whisperMsg, setWhisperMsg] = useState('');
  /** Pie: simular visitante + susurro; colapsados por defecto (siempre visible respuesta humana). */
  const [extraOperatorOptionsOpen, setExtraOperatorOptionsOpen] = useState(false);
  /** id del mensaje del agente para el formulario "corrección a la guía" */
  const [guideOpenFor, setGuideOpenFor] = useState<string | null>(null);
  const [guideUser, setGuideUser] = useState('');
  const [guideWrong, setGuideWrong] = useState('');
  const [guideNote, setGuideNote] = useState('');
  const [guideDesired, setGuideDesired] = useState('');
  const [guideContext, setGuideContext] = useState('');
  const [guideContextLoading, setGuideContextLoading] = useState(false);
  const [guideBusy, setGuideBusy] = useState(false);
  /** Añadir par a la guía del agente (requiere agente vinculado a la conversación). */
  const [addGuideToAgent, setAddGuideToAgent] = useState(true);
  const [ratingBusyId, setRatingBusyId] = useState<string | null>(null);
  const [contactBlocked, setContactBlocked] = useState(false);
  const [hasContactBlacklistPlugin, setHasContactBlacklistPlugin] = useState(false);
  const [blacklistOrigin, setBlacklistOrigin] = useState<{
    channel: string;
    channelIntegrationId: string | null;
    externalThreadId: string | null;
    visitorName: string | null;
    integrationDisplayName: string | null;
  } | null>(null);
  const [blacklistModalOpen, setBlacklistModalOpen] = useState(false);
  const [blacklistScope, setBlacklistScope] = useState<ContactBlacklistScope>('thread');
  const [blacklistLabel, setBlacklistLabel] = useState('');
  const [blacklistBusy, setBlacklistBusy] = useState(false);
  const { can } = useTenantPermissions();
  const { selectedAgentId } = useAgentSelector();
  const canManageContactBlacklist = can('web.conversations.write');
  const canSeeAgentModel = can('web.agents.write');
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const typingIdleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visitorTypingIdleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visitorSendingRef = useRef(false);
  /** Evita bajar al fondo en cada refresh del polling; solo al abrir otra conversación. */
  const lastAutoScrollConvId = useRef<string | null>(null);

  const signalOperatorTyping = useCallback(
    (active: boolean) => {
      if (!id) return;
      void apiPost(`/api/console/conversations/${id}/operator-typing`, { active }, token).catch(() => {});
    },
    [id, token]
  );

  const signalVisitorTyping = useCallback(
    (active: boolean) => {
      if (!id) return;
      void apiPost(`/api/console/conversations/${id}/visitor-typing`, { active }, token).catch(() => {});
    },
    [id, token]
  );

  /** Hora de referencia al render (agrupar por día y “Hoy” / “Ayer” correctos al abrir el chat). */
  const chatNow = new Date();

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await apiGet<Conv>(`/api/console/conversations/${id}`, token);
      setConv(data);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [id, token]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 2000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    setExtraOperatorOptionsOpen(false);
    setGuideOpenFor(null);
    setVisitorFiles([]);
    setBlacklistModalOpen(false);
  }, [id]);

  const loadContactBlacklistStatus = useCallback(async () => {
    if (!id) return;
    try {
      const data = await apiGet<{ blocked: boolean; origin: typeof blacklistOrigin }>(
        `/api/console/conversations/${id}/contact-blacklist/status`,
        token,
        { showErrorGrowl: false }
      );
      setContactBlocked(data.blocked);
      setBlacklistOrigin(data.origin);
    } catch {
      setContactBlocked(false);
      setBlacklistOrigin(null);
    }
  }, [id, token]);

  useEffect(() => {
    void loadContactBlacklistStatus();
  }, [loadContactBlacklistStatus]);

  useEffect(() => {
    const qs = selectedAgentId ? `?agentId=${encodeURIComponent(selectedAgentId)}` : '';
    void apiGet<{ hasPlugin: boolean }>(`/api/console/contact-blacklist/has-plugin${qs}`, token, { showErrorGrowl: false })
      .then((d) => setHasContactBlacklistPlugin(Boolean(d.hasPlugin)))
      .catch(() => setHasContactBlacklistPlugin(false));
  }, [token, selectedAgentId]);

  useEffect(() => {
    return () => {
      if (!id) return;
      void apiPost(`/api/console/conversations/${id}/operator-typing`, { active: false }, token).catch(() => {});
    };
  }, [id, token]);

  useLayoutEffect(() => {
    if (!id || !conv || conv.id !== id) return;
    if (lastAutoScrollConvId.current === id) return;
    lastAutoScrollConvId.current = id;
    const el = messagesScrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [id, conv]);

  useLayoutEffect(() => {
    if (!mensajeId || !conv || conv.id !== id) return;
    document.getElementById(`mensaje-${mensajeId}`)?.scrollIntoView({ block: 'center' });
  }, [mensajeId, conv, id]);

  useLayoutEffect(() => {
    if (!conv?.visitorTyping) return;
    const el = messagesScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [conv?.visitorTyping]);

  const setMode = async (controlMode: 'auto' | 'human') => {
    if (!id) return;
    setErr('');
    try {
      await apiPatch(`/api/console/conversations/${id}`, { controlMode }, token, {
        successMessage: 'Modo de conversación actualizado',
      });
      void load();
      refreshList();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const sendVisitor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || visitorSendingRef.current) return;
    if (!visitorMsg.trim() && visitorFiles.length === 0) return;
    setErr('');
    visitorSendingRef.current = true;
    try {
      const attachments =
        visitorFiles.length > 0 ? await filesToDemoAttachmentPayload(visitorFiles) : undefined;
      signalVisitorTyping(false);
      if (visitorTypingIdleRef.current) {
        clearTimeout(visitorTypingIdleRef.current);
        visitorTypingIdleRef.current = null;
      }
      await apiPost(
        `/api/console/conversations/${id}/visitor-messages`,
        { content: visitorMsg.trim(), ...(attachments?.length ? { attachments } : {}) },
        token,
        { successMessage: 'Mensaje de prueba enviado' }
      );
      setVisitorMsg('');
      setVisitorFiles([]);
      void load();
      refreshList();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      visitorSendingRef.current = false;
    }
  };

  const sendAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !agentMsg.trim()) return;
    setErr('');
    try {
      await apiPost(
        `/api/console/conversations/${id}/agent-messages`,
        { content: agentMsg.trim() },
        token,
        { successMessage: 'Mensaje enviado al visitante' }
      );
      signalOperatorTyping(false);
      if (typingIdleRef.current) {
        clearTimeout(typingIdleRef.current);
        typingIdleRef.current = null;
      }
      setAgentMsg('');
      void load();
      refreshList();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const sendWhisper = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !whisperMsg.trim()) return;
    setErr('');
    try {
      await apiPost(
        `/api/console/conversations/${id}/whisper-messages`,
        { content: whisperMsg.trim() },
        token,
        { successMessage: 'Susurro guardado' }
      );
      setWhisperMsg('');
      void load();
      refreshList();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const openGuideForMessage = (m: Msg, msgIdx: number) => {
    if (!conv) return;
    if (guideOpenFor === m.id) {
      setGuideOpenFor(null);
      return;
    }
    setAddGuideToAgent(Boolean(learningTargetAgent(conv)));
    setGuideUser(lastVisitorTextBefore(conv.messages, msgIdx));
    setGuideWrong(m.content);
    setGuideNote(m.operatorFeedbackComment ?? '');
    setGuideDesired(m.operatorFeedbackDesired ?? '');
    setGuideContext('');
    setGuideOpenFor(m.id);
  };

  const rateMessageGood = async (messageId: string) => {
    if (!id) return;
    setRatingBusyId(messageId);
    setErr('');
    setGuideOpenFor(null);
    try {
      const data = await apiPatch<{
        shortcutCreated: boolean;
        shortcutId: string | null;
        shortcutSkipped?: boolean;
      }>(`/api/console/conversations/${id}/messages/${messageId}`, { operatorRating: 'good' }, token);
      if (data.shortcutCreated) {
        showGrowl('Respuesta útil: añadida como atajo semántico del agente', 'success');
      } else {
        showGrowl(
          data.shortcutSkipped
            ? 'Respuesta marcada como útil (no hay agente vinculado; no se creó atajo)'
            : 'Respuesta marcada como útil',
          'success'
        );
      }
      void load();
      refreshList();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setRatingBusyId(null);
    }
  };

  const clearMessageRating = async (messageId: string) => {
    if (!id) return;
    setRatingBusyId(messageId);
    setErr('');
    try {
      await apiPatch(
        `/api/console/conversations/${id}/messages/${messageId}`,
        { operatorRating: null },
        token
      );
      setGuideOpenFor(null);
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setRatingBusyId(null);
    }
  };

  const submitGuide = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !guideOpenFor) return;
    const note = guideNote.trim();
    const desired = guideDesired.trim();
    if (!note && !desired) {
      setErr('Indicá qué estuvo mal o cómo debería haber respondido el asistente (al menos uno de los dos).');
      return;
    }
    const wantGuide = addGuideToAgent && Boolean(conv && learningTargetAgent(conv));
    if (wantGuide && !guideUser.trim()) {
      setErr('Completá a qué mensaje del visitante aplica, o rellená el campo de pregunta del visitante.');
      return;
    }
    setGuideBusy(true);
    setErr('');
    try {
      await apiPatch(
        `/api/console/conversations/${id}/messages/${guideOpenFor}`,
        {
          operatorRating: 'bad',
          operatorFeedbackDesired: desired || undefined,
          operatorFeedbackComment: note || undefined,
          userMessage: guideUser.trim() || undefined,
          applicationContext: guideContext.trim() || undefined,
          addToAgentGuide: wantGuide,
        },
        token,
        {
          successMessage: wantGuide
            ? 'Corrección guardada y añadida a la guía del agente'
            : 'Corrección guardada en el mensaje',
        }
      );
      setGuideOpenFor(null);
      void load();
      refreshList();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setGuideBusy(false);
    }
  };

  useEffect(() => {
    if (!id || !guideOpenFor) {
      setGuideContextLoading(false);
      return;
    }
    let cancelled = false;
    setGuideContext('');
    setGuideContextLoading(true);
    void (async () => {
      try {
        const data = await apiGet<{ contextSummary: string }>(
          `/api/console/conversations/${id}/messages/${guideOpenFor}/learning-context`,
          token
        );
        if (!cancelled) setGuideContext(data.contextSummary ?? '');
      } catch {
        if (!cancelled) setGuideContext('');
      } finally {
        if (!cancelled) setGuideContextLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, guideOpenFor, token]);

  useEffect(() => {
    if (!guideOpenFor) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !guideBusy) setGuideOpenFor(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [guideOpenFor, guideBusy]);

  useEffect(() => {
    if (!guideOpenFor) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [guideOpenFor]);

  const submitContactBlacklist = async () => {
    if (!id || !canManageContactBlacklist) return;
    setBlacklistBusy(true);
    setErr('');
    try {
      await apiPost(
        `/api/console/conversations/${id}/contact-blacklist`,
        { scope: blacklistScope, label: blacklistLabel.trim() || undefined },
        token,
        { successMessage: 'Contacto agregado a la lista negra' }
      );
      setBlacklistModalOpen(false);
      setContactBlocked(true);
      void loadContactBlacklistStatus();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBlacklistBusy(false);
    }
  };

  const removeContactBlacklist = async () => {
    if (!id || !canManageContactBlacklist) return;
    setBlacklistBusy(true);
    setErr('');
    try {
      await apiPost(
        `/api/console/conversations/${id}/contact-blacklist/remove`,
        {},
        token,
        { successMessage: 'Contacto quitado de la lista negra' }
      );
      setContactBlocked(false);
      void loadContactBlacklistStatus();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBlacklistBusy(false);
    }
  };

  const scopeSummary = (scope: ContactBlacklistScope): string => {
    if (scope === 'channel') return `Todo el canal ${channelLabel(conv?.channel ?? '')}`;
    if (scope === 'integration') {
      const name = blacklistOrigin?.integrationDisplayName ?? 'integración vinculada';
      return `Toda la cuenta: ${name}`;
    }
    const ext = conv?.externalThreadId ?? blacklistOrigin?.externalThreadId;
    return ext ? `Solo este contacto (${ext})` : 'Solo este hilo (sin identificador externo)';
  };

  const chatRenderItems = useMemo(
    () => groupConsoleImageMessages(conv?.messages ?? []),
    [conv?.messages]
  );

  if (!id) return null;

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#f8fafc',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {err ? (
        <div style={{ padding: '8px 16px', background: '#fef2f2', color: '#b91c1c', fontSize: 13 }}>{err}</div>
      ) : null}
      {conv?.calendarDepositReview ? (
        <div
          style={{
            padding: '10px 16px',
            background: '#fef9c3',
            borderBottom: '1px solid #fde047',
            fontSize: 13,
            color: '#713f12',
            lineHeight: 1.45,
          }}
        >
          <strong>Seña — revisión humana pendiente.</strong> Hay comprobante asociado a una reserva de{' '}
          <strong>{conv.calendarDepositReview.customerName?.trim() || 'cliente'}</strong> (
          {conv.calendarDepositReview.resource?.name?.trim() || 'recurso'}). Confirmá o gestioná desde reservas.{' '}
          <Link
            to={`/app/agents/${conv.calendarDepositReview.virtualAgentId}/calendar/bookings?bookingId=${encodeURIComponent(conv.calendarDepositReview.id)}`}
            style={{ color: '#7c3aed', fontWeight: 600 }}
          >
            Abrir reservas
          </Link>
        </div>
      ) : null}
      {blacklistModalOpen && conv ? (
        <Modal
          isOpen
          onClose={() => !blacklistBusy && setBlacklistModalOpen(false)}
          title="Agregar a lista negra"
          size="sm"
          closeOnOverlayClick={!blacklistBusy}
          closeOnEscape={!blacklistBusy}
          showCloseButton
          footer={
            <div style={{ display: 'flex', gap: tokens.spacing[2], justifyContent: 'flex-end', width: '100%' }}>
              <Button type="button" variant="ghost" disabled={blacklistBusy} onClick={() => setBlacklistModalOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="danger"
                loading={blacklistBusy}
                disabled={blacklistBusy}
                onClick={() => void submitContactBlacklist()}
              >
                Confirmar
              </Button>
            </div>
          }
        >
          <p
            style={{
              margin: `0 0 ${tokens.spacing[4]}`,
              fontSize: tokens.typography.fontSize.sm,
              color: tokens.colors.neutral[600],
              lineHeight: tokens.typography.lineHeight.relaxed,
            }}
          >
            Canal: <strong>{channelLabel(conv.channel)}</strong>
            {blacklistOrigin?.integrationDisplayName ? (
              <>
                {' '}
                · Cuenta: <strong>{blacklistOrigin.integrationDisplayName}</strong>
              </>
            ) : null}
            {(conv.externalThreadId ?? blacklistOrigin?.externalThreadId) ? (
              <>
                <br />
                Identificador:{' '}
                <code
                  style={{
                    fontSize: tokens.typography.fontSize.xs,
                    fontFamily: tokens.typography.fontFamily.mono,
                    background: tokens.colors.neutral[100],
                    padding: '2px 6px',
                    borderRadius: tokens.borderRadius.sm,
                  }}
                >
                  {conv.externalThreadId ?? blacklistOrigin?.externalThreadId}
                </code>
              </>
            ) : null}
          </p>
          <fieldset style={{ border: 'none', padding: 0, margin: `0 0 ${tokens.spacing[4]}` }}>
            <legend
              style={{
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: tokens.typography.fontWeight.semibold,
                color: tokens.colors.neutral[800],
                marginBottom: tokens.spacing[2],
              }}
            >
              Alcance
            </legend>
            {(['thread', 'integration', 'channel'] as const).map((scope) => (
              <label
                key={scope}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: tokens.spacing[2],
                  fontSize: tokens.typography.fontSize.sm,
                  color: tokens.colors.neutral[700],
                  marginBottom: tokens.spacing[2],
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="blacklistScope"
                  checked={blacklistScope === scope}
                  onChange={() => setBlacklistScope(scope)}
                  style={{ marginTop: 3 }}
                />
                <span>{scopeSummary(scope)}</span>
              </label>
            ))}
          </fieldset>
          <Input
            label="Motivo (opcional, solo consola)"
            value={blacklistLabel}
            onChange={(e) => setBlacklistLabel(e.target.value)}
            fullWidth
            disabled={blacklistBusy}
          />
        </Modal>
      ) : null}
      {conv ? (
        <>
          <div
            style={{
              padding: '12px 18px',
              background: '#fff',
              borderBottom: '1px solid #e2e8f0',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                {/* Avatar */}
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                  {(conv.visitorName ?? 'V').slice(0, 2).toUpperCase()}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                    {conv.visitorName ?? 'Sin nombre'}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ConversationChannelIcon channel={conv.channel} size={12} />
                    {channelLabel(conv.channel)}
                    {conv.controlMode === 'human' && <span style={{ color: '#92400e', fontWeight: 600 }}> · Humano</span>}
                    {contactBlocked && <span style={{ color: '#b91c1c', fontWeight: 600 }}> · Lista negra</span>}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                {hasContactBlacklistPlugin && canManageContactBlacklist ? (
                  contactBlocked ? (
                    <button
                      type="button"
                      style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                      disabled={blacklistBusy}
                      onClick={() => void removeContactBlacklist()}
                    >
                      Quitar lista negra
                    </button>
                  ) : (
                    <button
                      type="button"
                      style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                      onClick={() => {
                        setBlacklistScope('thread');
                        setBlacklistLabel('');
                        setBlacklistModalOpen(true);
                      }}
                    >
                      Lista negra
                    </button>
                  )
                ) : null}
                {conv.controlMode === 'auto' ? (
                  <button type="button" style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => void setMode('human')}>
                    → Modo humano
                  </button>
                ) : (
                  <button type="button" style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #fde68a', background: '#fef3c7', color: '#92400e', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => void setMode('auto')}>
                    👤 Humano · Volver a auto
                  </button>
                )}
              </div>
            </div>
          </div>

          {conv.hasLeadQualificationPlugin ? (
            <div style={{ padding: '10px 16px 12px', borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
              <LeadQualityEditor
                conversationId={conv.id}
                token={token}
                canWrite={can('web.conversations.write')}
                compact
                states={conv.leadMode === 'internal' ? conv.leadStates : undefined}
                value={{
                  leadQuality: conv.leadQuality ?? null,
                  leadReason: conv.leadReason ?? null,
                  leadFitDetails: conv.leadFitDetails ?? [],
                  leadNote: conv.leadNote ?? null,
                  leadRatedSource: conv.leadRatedSource ?? null,
                }}
                onSaved={(next) => {
                  setConv((c) => (c ? { ...c, ...next } : c));
                  refreshList();
                }}
              />
            </div>
          ) : null}

          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              padding: '16px 20px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <div
                ref={messagesScrollRef}
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  padding: '16px 20px',
                  background: '#fafbfc',
                }}
              >
              {chatRenderItems.map((item) => {
                if (item.type === 'album') {
                  const prevMsg = item.firstIndex > 0 ? conv.messages[item.firstIndex - 1]! : null;
                  const showDaySep =
                    !prevMsg || localDayKey(item.timestamp) !== localDayKey(prevMsg.timestamp);
                  return (
                    <Fragment key={item.id}>
                      {showDaySep ? (
                        <div
                          style={daySepWrap}
                          role="separator"
                          aria-label={daySeparatorLabel(item.timestamp, chatNow)}
                        >
                          <div style={daySepLine} aria-hidden />
                          <div style={daySepPill}>{daySeparatorLabel(item.timestamp, chatNow)}</div>
                          <div style={daySepLine} aria-hidden />
                        </div>
                      ) : null}
                      <div
                        style={{
                          marginBottom: 12,
                          textAlign: item.sender === 'visitor' ? 'left' : 'right',
                          ...(mensajeId && item.messageIds.includes(mensajeId)
                            ? { outline: `2px solid ${tokens.colors.primary[600]}`, outlineOffset: 4, borderRadius: 12 }
                            : {}),
                        }}
                      >
                        {item.messageIds.map((messageId) => (
                          <span key={messageId} id={`mensaje-${messageId}`} />
                        ))}
                        <div
                          style={{
                            display: 'inline-block',
                            maxWidth: 'min(280px, 78%)',
                            textAlign: 'left',
                          }}
                        >
                          <DemoImageAlbum images={item.images} maxWidth={280} />
                        </div>
                      </div>
                    </Fragment>
                  );
                }

                const m = conv.messages[item.index]!;
                const msgIdx = item.index;
                const isAgent = m.sender === 'agent' && !m.isWhisper;
                const isAudioMessage = Boolean(m.attachments?.some((a) => a.kind === 'audio'));
                const busy = ratingBusyId === m.id;
                const prevMsg = msgIdx > 0 ? conv.messages[msgIdx - 1]! : null;
                const showDaySep =
                  !prevMsg || localDayKey(m.timestamp) !== localDayKey(prevMsg.timestamp);
                const timeLine = messageDateTimeLine(m.timestamp, chatNow);
                const metaColor = m.isWhisper
                  ? 'rgba(120,53,15,0.85)'
                  : m.sender === 'visitor'
                    ? 'rgba(15,23,42,0.6)'
                    : 'rgba(255,255,255,0.82)';
                const bubble = (
                  <span
                    style={{
                      display: 'inline-block',
                      maxWidth: m.isWhisper ? '92%' : '78%',
                      minWidth: 0,
                      padding: '10px 14px',
                      borderRadius: 12,
                      borderBottomRightRadius: !m.isWhisper && m.sender === 'visitor' ? 4 : 12,
                      borderBottomLeftRadius: !m.isWhisper && m.sender === 'agent' ? 4 : 12,
                      background: m.isWhisper
                        ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                        : m.sender === 'visitor'
                          ? '#f1f5f9'
                          : '#7c3aed',
                      color: m.isWhisper ? '#78350f' : m.sender === 'visitor' ? '#0f172a' : '#fff',
                      border: m.isWhisper ? '1px dashed #d97706' : m.sender === 'visitor' ? '1px solid #e2e8f0' : undefined,
                      whiteSpace: 'pre-wrap',
                      textAlign: 'left',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 10,
                        marginBottom: 6,
                      }}
                    >
                      <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600, minWidth: 0, flex: '1 1 auto' }}>
                        {m.isWhisper ? 'Susurro (solo IA)' : m.sender}
                        {!m.isWhisper && m.agentSource
                          ? ` · ${agentSourceLabel(m.agentSource)}`
                          : ''}
                        {!m.isWhisper && m.sender === 'agent' && canSeeAgentModel && m.agentModel && m.usedModelFallback !== true
                          ? ` · ${formatAgentModel(m.agentModel)}`
                          : ''}
                      </div>
                      {timeLine ? (
                        <div style={{ ...messageTimeTop, color: metaColor }} title={String(new Date(m.timestamp))}>
                          {timeLine}
                        </div>
                      ) : null}
                    </div>
                    {canSeeAgentModel && m.sender === 'agent' && m.usedModelFallback === true && m.agentModelRequested && m.agentModel ? (
                      <div
                        style={{
                          fontSize: 10,
                          marginBottom: 6,
                          padding: '6px 8px',
                          background: 'rgba(254, 243, 199, 0.95)',
                          color: '#92400e',
                          borderRadius: 6,
                          lineHeight: 1.45,
                        }}
                      >
                        <strong>Reserva LLM</strong> (fallo del modelo pedido): intentó{' '}
                        <code style={{ fontSize: 10 }}>{m.agentModelRequested}</code> · respuesta con{' '}
                        <code style={{ fontSize: 10 }}>{m.agentModel}</code>
                      </div>
                    ) : null}
                    {m.sender === 'agent' && (m.ruleCorrections?.length || m.ruleAudit?.violations?.length) ? (
                      <div
                        style={{
                          fontSize: 10,
                          marginBottom: 6,
                          padding: '6px 8px',
                          background: 'rgba(224, 242, 254, 0.95)',
                          color: '#075985',
                          borderRadius: 6,
                          lineHeight: 1.45,
                        }}
                      >
                        {m.ruleCorrections?.map((c, i) => (
                          <div key={`c${i}`}>
                            <strong>Regla aplicada</strong>
                            {c.ruleTitle ? ` (${c.ruleTitle})` : ''}: «{c.before}» → «{c.after}»
                          </div>
                        ))}
                        {m.ruleAudit?.violations?.map((v) => (
                          <div key={v.ruleId} style={{ color: '#9a3412' }}>
                            <strong>Posible incumplimiento</strong>
                            {v.title ? ` (${v.title})` : ''}: {v.reason}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {m.sender === 'agent' && m.semanticShortcut && !m.attachments?.some((a) => a.kind === 'audio')
                      ? '* '
                      : null}
                    {(() => {
                      const audioAttachment = m.attachments?.find(a => a.kind === 'audio');
                      const isAudioMessage = Boolean(audioAttachment);
                      const transcriptionFailed = m.transcriptionStatus && m.transcriptionStatus !== 'success';
                      // Don't show content as text if it equals the transcription (avoid duplication)
                      const contentIsTranscription = isAudioMessage && m.audioTranscription && m.content === m.audioTranscription;
                      return (
                        <>
                          {isAudioMessage ? (
                            <span style={{ marginRight: 4 }} aria-label="Mensaje de audio">🎤</span>
                          ) : null}
                          {contentIsTranscription ? null : (m.sender === 'agent' ? formatMessage(m.content) : m.content)}
                          {isAudioMessage ? (
                            <div style={{ marginTop: 8 }}>
                              {m.attachments!
                                .filter(a => a.kind === 'audio')
                                .map((a, ai) => {
                                  const audioSrc = resolveWidgetMediaUrl(a.url || a.ref || '');
                                  if (!audioSrc || ['demo-recording', 'widget-upload', 'testchat-recording'].includes(a.url || a.ref || '')) {
                                    return (
                                      <span key={`${m.id}-audio-${ai}`} style={{ fontSize: tokens.typography.fontSize.xs, color: tokens.colors.neutral[500] }}>
                                        🎤 Audio
                                      </span>
                                    );
                                  }
                                  if (!/^(\/|https?:\/\/|data:)/.test(audioSrc)) {
                                    return (
                                      <span key={`${m.id}-audio-${ai}`} style={{ fontSize: tokens.typography.fontSize.xs, color: tokens.colors.neutral[500] }}>
                                        🎤 Audio
                                      </span>
                                    );
                                  }
                                  return (
                                    <InlineAudioPlayer
                                      key={`${m.id}-audio-${ai}`}
                                      src={audioSrc}
                                      durationSeconds={a.durationSeconds}
                                    />
                                  );
                                })}
                              {m.audioTranscription ? (
                                <div
                                  style={{
                                    marginTop: 6,
                                    padding: '6px 10px',
                                    borderRadius: tokens.borderRadius.sm,
                                    backgroundColor: tokens.colors.neutral[50],
                                    border: `1px solid ${tokens.colors.neutral[200]}`,
                                    fontSize: tokens.typography.fontSize.xs,
                                    color: tokens.colors.neutral[600],
                                    lineHeight: 1.45,
                                    fontStyle: 'italic',
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {m.audioTranscription}
                                </div>
                              ) : null}
                              {transcriptionFailed ? (
                                <div style={{ marginTop: 6 }}>
                                  {(() => {
                                    const statusLabels: Record<string, { label: string; icon: string; bgColor: string; textColor: string; borderColor: string }> = {
                                      failed: { label: 'Transcripción fallida', icon: '⚠️', bgColor: tokens.colors.warning.light, textColor: tokens.colors.warning.dark, borderColor: '#fde68a' },
                                      duration_exceeded: { label: 'Duración excedida', icon: '⚠️', bgColor: tokens.colors.warning.light, textColor: tokens.colors.warning.dark, borderColor: '#fde68a' },
                                      download_failed: { label: 'Error de descarga', icon: '⚠️', bgColor: tokens.colors.warning.light, textColor: tokens.colors.warning.dark, borderColor: '#fde68a' },
                                      disabled: { label: 'Transcripción deshabilitada', icon: 'ℹ️', bgColor: tokens.colors.neutral[100], textColor: tokens.colors.neutral[600], borderColor: tokens.colors.neutral[300] },
                                    };
                                    const info = statusLabels[m.transcriptionStatus!] ?? statusLabels.failed;
                                    return (
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 4,
                                          padding: '3px 8px',
                                          borderRadius: tokens.borderRadius.md,
                                          backgroundColor: info.bgColor,
                                          color: info.textColor,
                                          fontSize: tokens.typography.fontSize.xs,
                                          fontWeight: 500,
                                          border: `1px solid ${info.borderColor}`,
                                        }}
                                      >
                                        <span>{info.icon}</span>
                                        <span>{info.label}</span>
                                      </span>
                                    );
                                  })()}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          {!isAudioMessage && transcriptionFailed ? (
                            <div style={{ marginTop: 6 }}>
                              {(() => {
                                const statusLabels: Record<string, { label: string; icon: string; bgColor: string; textColor: string; borderColor: string }> = {
                                  failed: { label: 'Transcripción fallida', icon: '⚠️', bgColor: tokens.colors.warning.light, textColor: tokens.colors.warning.dark, borderColor: '#fde68a' },
                                  duration_exceeded: { label: 'Duración excedida', icon: '⚠️', bgColor: tokens.colors.warning.light, textColor: tokens.colors.warning.dark, borderColor: '#fde68a' },
                                  download_failed: { label: 'Error de descarga', icon: '⚠️', bgColor: tokens.colors.warning.light, textColor: tokens.colors.warning.dark, borderColor: '#fde68a' },
                                  disabled: { label: 'Transcripción deshabilitada', icon: 'ℹ️', bgColor: tokens.colors.neutral[100], textColor: tokens.colors.neutral[600], borderColor: tokens.colors.neutral[300] },
                                };
                                const info = statusLabels[m.transcriptionStatus!] ?? statusLabels.failed;
                                return (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 4,
                                      padding: '3px 8px',
                                      borderRadius: tokens.borderRadius.md,
                                      backgroundColor: info.bgColor,
                                      color: info.textColor,
                                      fontSize: tokens.typography.fontSize.xs,
                                      fontWeight: 500,
                                      border: `1px solid ${info.borderColor}`,
                                    }}
                                  >
                                    <span>{info.icon}</span>
                                    <span>{info.label}</span>
                                  </span>
                                );
                              })()}
                            </div>
                          ) : null}
                        </>
                      );
                    })()}
                    {m.attachments?.some((a) => a.kind !== 'audio') ? (
                      <div
                        style={{
                          marginTop: 10,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          maxWidth: 'min(360px, 100%)',
                        }}
                      >
                        {imageAttachmentsOf(m).length > 0 ? (
                          <DemoImageAlbum images={imageAttachmentsOf(m)} maxWidth={280} />
                        ) : null}
                        {m.attachments!
                          .filter((a) => a.kind !== 'audio' && a.kind !== 'image' && !(a.mimeType || '').toLowerCase().startsWith('image/'))
                          .map((a, ai) => {
                          const src = resolveWidgetMediaUrl(a.url ?? '');
                          if (a.kind === 'video') {
                            return (
                              <video
                                key={`${m.id}-att-${ai}`}
                                src={src}
                                controls
                                playsInline
                                style={{ width: '100%', borderRadius: 8, maxHeight: 280 }}
                              />
                            );
                          }
                          if (a.kind === 'document' || a.mimeType === 'application/pdf') {
                            return (
                              <a
                                key={`${m.id}-att-${ai}`}
                                href={src}
                                target="_blank"
                                rel="noreferrer"
                                download={a.caption || 'adjunto.pdf'}
                                style={{
                                  display: 'inline-block',
                                  padding: '8px 12px',
                                  borderRadius: 8,
                                  background: 'rgba(15,23,42,0.06)',
                                  color: '#1e293b',
                                  fontSize: 13,
                                  fontWeight: 600,
                                }}
                              >
                                {a.caption?.trim() ? `📄 ${a.caption}` : '📄 Ver PDF'}
                              </a>
                            );
                          }
                          return null;
                        })}
                      </div>
                    ) : null}
                  </span>
                );
                return (
                <Fragment key={m.id}>
                {showDaySep ? (
                  <div
                    style={daySepWrap}
                    role="separator"
                    aria-label={daySeparatorLabel(m.timestamp, chatNow)}
                  >
                    <div style={daySepLine} aria-hidden />
                    <div style={daySepPill}>{daySeparatorLabel(m.timestamp, chatNow)}</div>
                    <div style={daySepLine} aria-hidden />
                  </div>
                ) : null}
                <div
                  id={`mensaje-${m.id}`}
                  style={{
                    marginBottom: 12,
                    textAlign: m.isWhisper ? 'center' : m.sender === 'visitor' ? 'left' : 'right',
                    ...(mensajeId === m.id
                      ? { outline: `2px solid ${tokens.colors.primary[600]}`, outlineOffset: 4, borderRadius: 12 }
                      : {}),
                  }}
                >
                  {isAgent && !isAudioMessage ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-end',
                        gap: 6,
                        width: '100%',
                        maxWidth: '100%',
                      }}
                    >
                      {bubble}
                      <div
                        title="Valorar esta respuesta"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 3,
                          flexShrink: 0,
                          opacity: 0.48,
                          marginTop: 2,
                          transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.9';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '0.48';
                        }}
                      >
                        <button
                          type="button"
                          title="Respuesta útil (atajo semántico)"
                          disabled={busy}
                          onClick={() => void rateMessageGood(m.id)}
                          style={{
                            ...floatRateBtn,
                            color: m.operatorRating === 'good' ? '#15803d' : 'rgba(255,255,255,0.95)',
                            background:
                              m.operatorRating === 'good'
                                ? 'rgba(220,252,231,0.95)'
                                : 'rgba(255,255,255,0.2)',
                            border: m.operatorRating === 'good' ? '1px solid #86efac' : '1px solid rgba(255,255,255,0.3)',
                            boxShadow: m.operatorRating === 'good' ? '0 0 0 1px rgba(34,197,94,0.2)' : 'none',
                          }}
                        >
                          👍
                        </button>
                        <button
                          type="button"
                          title="A mejorar (corrección / guía)"
                          disabled={busy}
                          onClick={() => openGuideForMessage(m, msgIdx)}
                          style={{
                            ...floatRateBtn,
                            color: m.operatorRating === 'bad' ? '#fecaca' : 'rgba(255,255,255,0.95)',
                            background:
                              m.operatorRating === 'bad' ? 'rgba(127,29,29,0.5)' : 'rgba(255,255,255,0.2)',
                            border:
                              m.operatorRating === 'bad' ? '1px solid #f87171' : '1px solid rgba(255,255,255,0.3)',
                            boxShadow: guideOpenFor === m.id ? '0 0 0 2px rgba(255,255,255,0.45)' : 'none',
                          }}
                        >
                          👎
                        </button>
                        {(m.operatorRating === 'good' || m.operatorRating === 'bad') && !busy ? (
                          <button
                            type="button"
                            title="Quitar valoración"
                            onClick={() => void clearMessageRating(m.id)}
                            style={{ ...floatRateBtn, fontSize: 9, lineHeight: 1, padding: '1px 4px', background: 'rgba(255,255,255,0.15)' }}
                          >
                            ✕
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    bubble
                  )}
                </div>
                </Fragment>
                );
              })}
              {conv.visitorTyping ? (
                <div
                  style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 4 }}
                  aria-label="Visitante escribiendo"
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      gap: 5,
                      alignItems: 'center',
                      padding: '12px 16px',
                      borderRadius: 14,
                      borderBottomLeftRadius: 4,
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className="va-console-typing-dot"
                        style={{ animationDelay: `${i * 0.2}s` }}
                      />
                    ))}
                  </span>
                </div>
              ) : null}
              </div>
            </div>
          </div>

          <div
            style={{
              padding: '12px 20px 16px',
              background: '#fff',
              borderTop: '1px solid #e2e8f0',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                marginBottom: 6,
                flexWrap: 'wrap',
              }}
            >
              <strong style={{ fontSize: 12, color: '#475569' }}>Responder como humano</strong>
              <button
                type="button"
                onClick={() => setExtraOperatorOptionsOpen((o) => !o)}
                style={btnToggleExtras}
                aria-expanded={extraOperatorOptionsOpen}
              >
                {extraOperatorOptionsOpen ? '▼ Ocultar opciones' : '▶ Más opciones'}
              </button>
            </div>
            <form onSubmit={sendAgent}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  style={{ ...inp, flex: 1 }}
                  value={agentMsg}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAgentMsg(v);
                    if (typingIdleRef.current) {
                      clearTimeout(typingIdleRef.current);
                      typingIdleRef.current = null;
                    }
                    if (!id) return;
                    if (!v.trim()) {
                      signalOperatorTyping(false);
                      return;
                    }
                    signalOperatorTyping(true);
                    typingIdleRef.current = setTimeout(() => signalOperatorTyping(false), 2200);
                  }}
                  onBlur={() => {
                    if (typingIdleRef.current) {
                      clearTimeout(typingIdleRef.current);
                      typingIdleRef.current = null;
                    }
                    signalOperatorTyping(false);
                  }}
                  placeholder="Respuesta del operador…"
                />
                <button type="submit" style={btnPri}>
                  Enviar
                </button>
              </div>
            </form>
            {extraOperatorOptionsOpen ? (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                {conv.channel === 'testchat' ? (
                  <form onSubmit={sendVisitor} style={{ marginBottom: 12 }}>
                    <strong style={{ fontSize: 12, color: '#475569' }}>Simular visitante</strong>
                    <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <input
                        style={{ ...inp, flex: '1 1 200px', minWidth: 0 }}
                        value={visitorMsg}
                        onChange={(e) => {
                          const v = e.target.value;
                          setVisitorMsg(v);
                          if (visitorTypingIdleRef.current) {
                            clearTimeout(visitorTypingIdleRef.current);
                            visitorTypingIdleRef.current = null;
                          }
                          if (!v.trim()) {
                            signalVisitorTyping(false);
                            return;
                          }
                          signalVisitorTyping(true);
                          visitorTypingIdleRef.current = setTimeout(
                            () => signalVisitorTyping(false),
                            2200
                          );
                        }}
                        onBlur={() => {
                          if (visitorTypingIdleRef.current) {
                            clearTimeout(visitorTypingIdleRef.current);
                            visitorTypingIdleRef.current = null;
                          }
                          signalVisitorTyping(false);
                        }}
                        placeholder="Mensaje del visitante…"
                      />
                      <label style={{ fontSize: 12, color: '#64748b', flexShrink: 0 }}>
                        Adjuntos
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                          multiple
                          style={{ display: 'block', marginTop: 4, maxWidth: 220 }}
                          onChange={(e) => setVisitorFiles(Array.from(e.target.files ?? []))}
                        />
                      </label>
                      {visitorFiles.length > 0 ? (
                        <span style={{ fontSize: 11, color: '#64748b' }}>{visitorFiles.length} archivo(s)</span>
                      ) : null}
                      <button type="submit" style={btnPri}>
                        Enviar
                      </button>
                    </div>
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: '#64748b' }}>
                      Podés enviar solo imágenes/PDF como comprobante de seña si hay una reserva pendiente vinculada a este hilo.
                    </p>
                  </form>
                ) : (
                  <p
                    style={{
                      margin: '0 0 10px',
                      fontSize: 12,
                      color: '#64748b',
                      lineHeight: 1.5,
                    }}
                  >
                    La simulación del visitante solo está disponible en conversaciones <strong>Testchat</strong>. En este
                    canal los mensajes del visitante llegan desde la integración correspondiente.
                  </p>
                )}
                <form onSubmit={(e) => void sendWhisper(e)}>
                  <strong style={{ fontSize: 12, color: '#b45309' }}>Susurro al agente (no se muestra al visitante)</strong>
                  <p style={{ margin: '4px 0 6px', fontSize: 11, color: '#64748b', lineHeight: 1.45 }}>
                    Instrucciones internas: tono, datos o cómo encarar el próximo turno. En modo <strong>automático</strong>,
                    si el visitante acaba de escribir y aún no respondió la IA, se puede generar la respuesta al guardar.
                  </p>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <input
                      style={{ ...inp, flex: 1, border: '1px solid #fcd34d' }}
                      value={whisperMsg}
                      onChange={(e) => setWhisperMsg(e.target.value)}
                      placeholder="Ej.: Ofrecer plan anual, mencionar descuento 10%…"
                    />
                    <button
                      type="submit"
                      style={{ ...btnPri, background: '#d97706' }}
                    >
                      Susurro
                    </button>
                  </div>
                </form>
              </div>
            ) : null}
          </div>
          {conv && guideOpenFor
            ? createPortal(
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="guide-modal-title"
                  style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 10000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 20,
                    boxSizing: 'border-box',
                    background: 'rgba(15, 23, 42, 0.55)',
                  }}
                  onClick={() => {
                    if (!guideBusy) setGuideOpenFor(null);
                  }}
                >
                  <form
                    onClick={(e) => e.stopPropagation()}
                    onSubmit={(e) => void submitGuide(e)}
                    style={{
                      maxWidth: 480,
                      width: '100%',
                      maxHeight: 'min(90vh, 720px)',
                      overflow: 'auto',
                      padding: 20,
                      borderRadius: 12,
                      background: '#f8fafc',
                      border: '1px solid #ddd6fe',
                      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                      textAlign: 'left',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 12,
                        marginBottom: 12,
                      }}
                    >
                      <h2
                        id="guide-modal-title"
                        style={{ fontSize: 16, margin: 0, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}
                      >
                        Corregir aprendizaje
                      </h2>
                      <button
                        type="button"
                        aria-label="Cerrar"
                        disabled={guideBusy}
                        onClick={() => setGuideOpenFor(null)}
                        style={{
                          border: 'none',
                          background: 'rgba(148,163,184,0.25)',
                          color: '#475569',
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          cursor: guideBusy ? 'not-allowed' : 'pointer',
                          fontSize: 20,
                          lineHeight: 1,
                          flexShrink: 0,
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <p style={{ fontSize: 12, color: '#334155', margin: '0 0 10px', lineHeight: 1.45 }}>
                      {learningTargetAgent(conv) ? (
                        <>
                          Se guarda solo en la <strong>guía</strong> de <strong>{learningTargetAgent(conv)!.name}</strong>{' '}
                          (few-shot en el prompt; no crea atajo semántico). El agente la usará en otros chats si el tema
                          encaja.
                        </>
                      ) : (
                        <>
                          Sin agente al que vincular la guía: la corrección queda en este mensaje. Configurá agente o
                          flujo con nodo agente, o añadí pares en <strong>Agentes</strong>.
                        </>
                      )}
                    </p>
                    {learningTargetAgent(conv) ? (
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 12,
                          color: '#334155',
                          marginBottom: 10,
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={addGuideToAgent}
                          onChange={(e) => setAddGuideToAgent(e.target.checked)}
                          disabled={guideBusy}
                        />
                        Añadir a la guía (todas las conversaciones que usen este agente)
                      </label>
                    ) : null}
                    <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                      Contexto en el que se aplica
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: '#94a3b8', lineHeight: 1.4 }}>
                        Resumen del hilo (últimos 20 mensajes). El agente lo usa para saber cuándo aplicar esta guía.
                      </p>
                      {guideContextLoading ? (
                        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
                          Generando contexto…
                        </p>
                      ) : (
                        <textarea
                          style={{ ...inp, width: '100%', boxSizing: 'border-box', minHeight: 72, marginTop: 6 }}
                          value={guideContext}
                          onChange={(e) => setGuideContext(e.target.value)}
                          placeholder="Se carga al abrir; podés editarlo antes de guardar"
                          disabled={guideBusy || guideContextLoading}
                        />
                      )}
                    </label>
                    <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                      Pregunta del visitante a la que aplica{addGuideToAgent && learningTargetAgent(conv) ? ' *' : ''}
                      <textarea
                        style={{ ...inp, width: '100%', boxSizing: 'border-box', minHeight: 56, marginTop: 4 }}
                        value={guideUser}
                        onChange={(e) => setGuideUser(e.target.value)}
                        placeholder="Se rellena con el mensaje del visitante anterior; podés ajustar el tipo de pregunta"
                        disabled={guideBusy}
                      />
                    </label>
                    <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                      Respuesta que viste
                      <textarea
                        style={{
                          ...inp,
                          width: '100%',
                          boxSizing: 'border-box',
                          minHeight: 48,
                          marginTop: 4,
                          opacity: 0.92,
                        }}
                        value={guideWrong}
                        readOnly
                        disabled
                      />
                    </label>
                    <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                      Qué estuvo mal (opcional, va como nota en la guía)
                      <textarea
                        style={{ ...inp, width: '100%', boxSizing: 'border-box', minHeight: 44, marginTop: 4 }}
                        value={guideNote}
                        onChange={(e) => setGuideNote(e.target.value)}
                        placeholder="P. ej. faltó el dato X, tono inadecuado, información incorrecta…"
                        disabled={guideBusy}
                      />
                    </label>
                    <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                      Cómo debería haber respondido el asistente (opcional)
                      <textarea
                        style={{ ...inp, width: '100%', boxSizing: 'border-box', minHeight: 80, marginTop: 4 }}
                        value={guideDesired}
                        onChange={(e) => setGuideDesired(e.target.value)}
                        placeholder="Texto o pauta de respuesta deseada para aprender este caso"
                        disabled={guideBusy}
                      />
                    </label>
                    <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 10px', lineHeight: 1.45 }}>
                      Completá al menos uno: qué estuvo mal o cómo debería haber respondido.
                    </p>
                    <div
                      style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}
                    >
                      <button
                        type="button"
                        style={btnGuideCancel}
                        disabled={guideBusy}
                        onClick={() => setGuideOpenFor(null)}
                      >
                        Cancelar
                      </button>
                      <button type="submit" style={btnPri} disabled={guideBusy}>
                        {guideBusy ? 'Guardando…' : 'Guardar evaluación'}
                      </button>
                    </div>
                  </form>
                </div>,
                document.body
              )
            : null}
        </>
      ) : (
        <div style={{ padding: 24, color: '#64748b' }}>Cargando…</div>
      )}
    </div>
  );
}

const inp: React.CSSProperties = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  fontSize: 14,
};

const btnPri: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  border: 'none',
  background: '#7c3aed',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};

const btnToggleExtras: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 6,
  border: '1px solid #e2e8f0',
  background: '#f8fafc',
  color: '#475569',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  flexShrink: 0,
};

const btnGuideCancel: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#475569',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
};

const floatRateBtn: React.CSSProperties = {
  width: 30,
  height: 28,
  padding: 0,
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.35)',
  background: 'rgba(255,255,255,0.22)',
  fontSize: 15,
  lineHeight: 1,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};
